import { useDefaultDataLayer } from '../useDataLayer';

interface UseButtonsAndLinksTrackingOptions {
  componentName?: string;
  location?: string;
}

type TrackingEventType = 'click_button' | 'click_link';

interface UseButtonsAndLinksTrackingReturn {
  trackState: (
    linkText: string,
    intentText?: string | null,
    additionalData?: Record<string, unknown>,
    event?: TrackingEventType
  ) => void;
  trackButtonState: (
    buttonText: string,
    intent?: string | null,
    additionalData?: Record<string, unknown>
  ) => void;
  trackLinkState: (
    linkText: string,
    intent?: string | null,
    additionalData?: Record<string, unknown>
  ) => void;
}

/**
 * Hook for tracking buttons/links interactions
 */
export function useButtonsAndLinksTracking(
  options: UseButtonsAndLinksTrackingOptions = {}
): UseButtonsAndLinksTrackingReturn {
  const { componentName = '', location = 'body' } = options;
  const { registerInteraction } = useDefaultDataLayer();

  /** Track button/link state change */
  const trackState = (
    linkText: string,
    intentText: string | null = null,
    additionalData: Record<string, unknown> = {},
    event: TrackingEventType = 'click_button'
  ): void => {
    const link_text = linkText?.toLowerCase() || '';
    const intent = intentText || link_text.replaceAll(/\s+/g, '_') || '';

    registerInteraction({
      event,
      link_text,
      component: componentName,
      intent,
      event_location: location,
      ...additionalData,
    });
  };

  /** Track button state change */
  const trackButtonState = (
    buttonText: string,
    intent: string | null = null,
    additionalData: Record<string, unknown> = {}
  ): void => {
    trackState(buttonText, intent, { ...additionalData }, 'click_button');
  };

  /** Track link state change */
  const trackLinkState = (
    linkText: string,
    intent: string | null = null,
    additionalData: Record<string, unknown> = {}
  ): void => {
    trackState(linkText, intent, { ...additionalData }, 'click_link');
  };

  return {
    trackState,
    trackButtonState,
    trackLinkState,
  };
}
